import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Table } from 'antd';
import { InfiniteScroll, Spin, WarningCard } from 'common/components';
import { EMode } from 'common/const';
import { showMessage } from 'common/helpers';
import { useLegalEntityContext } from 'common/context';
import { LIST_LIMIT_0 } from 'common/config';
import { Bin, Plus } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { EditLegalEntityCard, LegalEntityCard } from 'entities/LegalEntity/components';
import { ILegal } from 'entities/LegalEntity/LegalEntity.models';
import { renderLegalEntityListSettingsRecords } from 'entities/Settings/Settings.helper';
import {
  deleteLegal,
  getLegalById,
  getLegalList,
  selectIsLegalLoading,
  selectLegalList,
  setLegalList,
} from 'entities/LegalEntity/Legal.slice';

interface IComponentProps {
  header: ReactNode;
}

export const SettingsAccountLegalEntityListTab: FC<IComponentProps> = ({ header }) => {
  const dispatch = useAppDispatch();

  const legalList = useAppSelector(selectLegalList);
  const isLegalLoading = useAppSelector(selectIsLegalLoading);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [selectedLegalEntity, setSelectedLegalEntity] = useState<ILegal | null>(null);
  const { setEditLegalEntityCardMode, setOpenLegalEntityCard, setOpenEditLegalEntityCard } = useLegalEntityContext();

  const onCreateLegalEntityClick = async () => {
    setEditLegalEntityCardMode(EMode.Create);
    setOpenEditLegalEntityCard(true);
  };

  const onEditLegalEntityClick = async (id: number) => {
    dispatch(
      getLegalById({
        id,
        onSuccess: () => {
          setEditLegalEntityCardMode(EMode.Edit);
          setOpenEditLegalEntityCard(true);
        },
      }),
    );
  };

  const onDeleteLegalEntityClick = (record: ILegal) => {
    setSelectedLegalEntity(record);
    setOpenWarningCard(true);
  };

  const onDeleteLegalEntityConfirmClick = () => {
    if (selectedLegalEntity) {
      dispatch(
        deleteLegal({
          id: selectedLegalEntity.id,
          onSuccess: () => {
            showMessage({
              content: `Юридическое лицо «${selectedLegalEntity.name}» удалено.`,
              icon: <Bin className="icon-bin-dark-grey" />,
            });
            setOpenWarningCard(false);
          },
        }),
      );
    }
  };

  useEffect(() => {
    const fetch = async () => {
      dispatch(setLegalList([]));
      await dispatch(getLegalList({ limit: LIST_LIMIT_0 }));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  return (
    <>
      <InfiniteScroll>
        {header}

        <div className="mb-6">
          <Button className="button-sm primary" icon={<Plus className="icon-plus-white" />} onClick={onCreateLegalEntityClick}>
            Добавить новое
          </Button>
        </div>

        <Table
          className={`table-hover ${!legalList.length && 'table-empty'} mb-19 account-legal-entity-list-settings__table`}
          showHeader={false}
          pagination={false}
          size="middle"
          dataSource={legalList.map((legalEntity) => ({ ...legalEntity, key: legalEntity.id }))}
          columns={renderLegalEntityListSettingsRecords(onEditLegalEntityClick, onDeleteLegalEntityClick)}
          locale={{
            emptyText: fetchLoading ? (
              <Spin />
            ) : (
              <div className="text-body color-dark-grey">Юридические лица ещё не указаны.</div>
            ),
          }}
          loading={isLegalLoading}
          onRow={({ id }) => ({
            onClick: () => {
              dispatch(
                getLegalById({
                  id,
                  onSuccess: () => setOpenLegalEntityCard(true),
                }),
              );
            },
          })}
        />
      </InfiniteScroll>

      <LegalEntityCard />

      <EditLegalEntityCard />

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите удалить юр. лицо?"
        subtitle="Юридическое лицо будет отвязано от подразделений и корзин пользователей."
        confirmBtnTitle="Удалить"
        loading={isLegalLoading}
        onConfirm={onDeleteLegalEntityConfirmClick}
        onClose={() => setOpenWarningCard(false)}
      />
    </>
  );
};
